import { createRouter } from '@tanstack/react-router';
import { PageLoader } from '@opengoal/ui';
import { queryClient } from '@/app/queryClient';
import { appLayoutRoute, authLayoutRoute, publicLayoutRoute, rootRoute } from '@/app/routes/roots';
import { authRoutes, publicAuthRoutes } from '@/modules/auth';
import { billingRoutes } from '@/modules/billing';
import { clubsRoutes } from '@/modules/clubs';
import { contractsRoutes } from '@/modules/contracts';
import { dashboardRoutes } from '@/modules/dashboard';
import { dealsRoutes } from '@/modules/deals';
import { offboardingRoutes } from '@/modules/offboarding';
import { onboardingRoutes } from '@/modules/onboarding';
import { playersRoutes } from '@/modules/players';
import { requestsRoutes } from '@/modules/requests';
import { settingsRoutes } from '@/modules/settings';
import { tasksRoutes } from '@/modules/tasks';
import { transferWindowsRoutes } from '@/modules/transfer-windows';

/**
 * The route tree.
 *
 * This is the ONLY file that knows about every module, and all it does is
 * assemble their exported route lists. Modules define their own routes next to
 * their own pages, so adding a feature never means editing a central table of
 * paths that everyone conflicts on.
 */
const routeTree = rootRoute.addChildren([
  authLayoutRoute.addChildren(authRoutes),
  publicLayoutRoute.addChildren(publicAuthRoutes),
  appLayoutRoute.addChildren([
    ...dashboardRoutes,
    ...playersRoutes,
    ...onboardingRoutes,
    ...offboardingRoutes,
    ...clubsRoutes,
    ...transferWindowsRoutes,
    ...dealsRoutes,
    ...contractsRoutes,
    ...requestsRoutes,
    ...tasksRoutes,
    ...settingsRoutes,
    ...billingRoutes,
  ]),
]);

export const router = createRouter({
  routeTree,
  context: { queryClient },
  // Start loading a route's chunk when the user hovers its link. Costs nothing
  // when they don't click and removes the visible delay when they do.
  defaultPreload: 'intent',
  defaultPreloadStaleTime: 0,
  defaultPendingComponent: PageLoader,
  scrollRestoration: true,
});

// Registers the router's types globally, which is what makes `useSearch`,
// `useParams` and `navigate({ to: ... })` type-checked against the real tree
// rather than accepting any string.
declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
